import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../Network";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { handleAuthError } from "../../utils";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import withAuth from "../withAuth";
import LearningPathMetaFields, { emptyLearningPathMeta, LearningPathIcon } from "./LearningPathMetaFields";
import { learningPathErrorMessage, LearningPathErrorAlert } from "./learningPathErrors";

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-");

// AI Tools > Learning Paths: journeys built from existing curricula, shown on the AI app's Learning Path page.
const ManageAiLearningPaths = () => {
  document.title = "Learning Paths | RYD Admin";
  const readOnly = isMarketerReadOnly();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [categories, setCategories] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [newPath, setNewPath] = useState(emptyLearningPathMeta());
  const [creating, setCreating] = useState(false);
  const [pathToDelete, setPathToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Inline errors for actions that change data (shown until dismissed or retried).
  const [listError, setListError] = useState("");
  const [createError, setCreateError] = useState("");
  const [deleteError, setDeleteError] = useState("");

  const fetchPaths = async () => {
    try {
      setLoading(true);
      const [pathsRes, catsRes] = await Promise.all([
        axios.get(`${baseUrl}/admin/learning-path/all`),
        axios.get(`${baseUrl}/admin/learning-path/category/all`),
      ]);
      setPaths(pathsRes?.data?.data || []);
      setCategories(catsRes?.data?.data || []);
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to load learning paths");
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (item) => {
    try {
      setListError("");
      setTogglingId(item.id);
      const nextValue = !item.isVisible;
      const response = await axios.put(`${baseUrl}/admin/learning-path/visibility/${item.id}`, { isVisible: nextValue });
      const updated = response?.data?.data;
      setPaths((prev) => prev.map((p) => (p.id === item.id ? { ...p, ...updated, units: undefined } : p)));
      toast.success(nextValue ? "Learning path is now visible to learners" : "Learning path hidden from learners");
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, `Couldn't ${item.isVisible ? "hide" : "publish"} "${item.title}".`);
      setListError(`${item.isVisible ? "Hide" : "Publish"} "${item.title}": ${message}`);
      toast.error(message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async () => {
    if (!newPath.title.trim()) {
      setCreateError("Title is required.");
      return;
    }
    try {
      setCreateError("");
      setCreating(true);
      const payload = {
        ...newPath,
        level: newPath.level || null,
        subcategoryId: newPath.subcategoryId === "" || newPath.subcategoryId == null ? null : Number(newPath.subcategoryId),
      };
      const response = await axios.post(`${baseUrl}/admin/learning-path`, payload);
      const created = response?.data?.data;
      toast.success("Learning path created. Now add units and curricula.");
      setCreateModal(false);
      setNewPath(emptyLearningPathMeta());
      if (created?.id) navigate(`/ai/learning-paths/${created.id}`);
      else fetchPaths();
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't create the learning path.");
      setCreateError(message);
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const formatPlacement = (item) => {
    if (item?.category?.title && item?.subcategory?.title) {
      return `${item.category.title} → ${item.subcategory.title}`;
    }
    if (item?.subcategory?.title) return item.subcategory.title;
    return "—";
  };

  const formatLevel = (level) => {
    if (!level) return "—";
    return String(level).charAt(0).toUpperCase() + String(level).slice(1);
  };

  const handleDelete = async () => {
    if (!pathToDelete) return;
    try {
      setDeleteError("");
      setDeleting(true);
      await axios.delete(`${baseUrl}/admin/learning-path/${pathToDelete.id}`);
      setPaths((prev) => prev.filter((p) => p.id !== pathToDelete.id));
      toast.success("Learning path deleted");
      setPathToDelete(null);
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't delete the learning path.");
      setDeleteError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchPaths();
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI Tools" breadcrumbItem="Learning Paths" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <p className="text-muted mb-0">
                    Paths sit under a category → subcategory (e.g. Coding → JavaScript). Visible paths appear on the AI Tutor Learning Path page.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Button color="secondary" outline tag={Link} to="/ai/learning-path-categories">
                      Manage categories
                    </Button>
                    {!readOnly && (
                      <Button color="primary" onClick={() => { setCreateError(""); setCreateModal(true); }}>
                        <i className="mdi mdi-plus me-1" /> New learning path
                      </Button>
                    )}
                  </div>
                </div>
                <LearningPathErrorAlert message={listError} onDismiss={() => setListError("")} />
                {loading ? (
                  <div className="text-center py-5">Loading...</div>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Level</th>
                          <th>Icon</th>
                          <th>Units</th>
                          <th>Steps</th>
                          <th>Order</th>
                          <th>Updated</th>
                          <th>Visibility</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paths.map((item, index) => {
                          const notLive = item.stepCount - item.liveStepCount;
                          return (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>
                                <Link to={`/ai/learning-paths/${item.id}`} className="fw-semibold">{item.title}</Link>
                              </td>
                              <td className="small">{formatPlacement(item)}</td>
                              <td>{formatLevel(item.level)}</td>
                              <td><LearningPathIcon icon={item.icon} /></td>
                              <td>{item.unitCount}</td>
                              <td>
                                {item.stepCount}
                                {notLive > 0 && (
                                  <Badge color="warning" className="ms-2" title="Steps whose curriculum is hidden or deleted are not shown to learners">
                                    {notLive} not live
                                  </Badge>
                                )}
                              </td>
                              <td>{item.sortOrder}</td>
                              <td>{formatDate(item.updatedAt)}</td>
                              <td>
                                {readOnly ? (
                                  <span className="text-muted">{item.isVisible ? "Visible" : "Hidden"}</span>
                                ) : (
                                  <Button
                                    type="button"
                                    color={item.isVisible ? "warning" : "success"}
                                    outline={!!item.isVisible}
                                    size="sm"
                                    disabled={togglingId === item.id}
                                    onClick={() => toggleVisibility(item)}
                                  >
                                    {togglingId === item.id ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                        Processing…
                                      </>
                                    ) : item.isVisible ? "Hide" : "Show"}
                                  </Button>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-2">
                                  <Button type="button" color="info" outline size="sm" onClick={() => navigate(`/ai/learning-paths/${item.id}`)}>
                                    {readOnly ? "View" : "Edit"}
                                  </Button>
                                  {!readOnly && (
                                    <Button type="button" color="danger" outline size="sm" onClick={() => { setDeleteError(""); setPathToDelete(item); }}>
                                      Delete
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!paths.length && (
                          <tr>
                            <td colSpan={11} className="text-center py-4">No learning paths yet</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={createModal} toggle={() => !creating && setCreateModal(false)} size="lg">
        <ModalHeader toggle={() => !creating && setCreateModal(false)}>New learning path</ModalHeader>
        <ModalBody>
          <LearningPathErrorAlert message={createError} onDismiss={() => setCreateError("")} />
          <LearningPathMetaFields value={newPath} onChange={setNewPath} disabled={creating} categories={categories} />
          <p className="text-muted small mt-3 mb-0">
            New paths start hidden. Assign a subcategory (and level) so they appear under the right topic in the AI app.
          </p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={() => setCreateModal(false)} className="me-2" disabled={creating}>Cancel</Button>
            <Button color="primary" onClick={handleCreate} disabled={creating}>
              {creating ? <><Spinner size="sm" className="me-2" />Creating…</> : "Create and add curricula"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!pathToDelete} toggle={() => !deleting && setPathToDelete(null)}>
        <ModalHeader toggle={() => !deleting && setPathToDelete(null)}>Confirm delete</ModalHeader>
        <ModalBody>
          <LearningPathErrorAlert message={deleteError} onDismiss={() => setDeleteError("")} />
          <p>
            Delete the learning path <strong>{pathToDelete?.title}</strong>? Learners will no longer see it.
            The curricula themselves are not affected.
          </p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={() => setPathToDelete(null)} className="me-2" disabled={deleting}>Cancel</Button>
            <Button color="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Spinner size="sm" className="me-2" />Deleting…</> : "Delete learning path"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default withAuth(ManageAiLearningPaths);
